import { ACLoading, ACSpinner } from '@antech-consulting/components';
import { Divider } from 'components/Divider/Divider';
import Modal from 'components/Modal/Modal';
import { ErrorMessage } from 'constants/Errors/ErrorTypes';
import { FORM_ELEMENT_ADDRESS, FORM_ELEMENT_NAME, IS_EMPLOYED } from 'constants/FormConstants';
import { ROLE_SYSTEM } from 'constants/RolesConstants';
import { TableStateProps } from 'constants/TypeConstants';
import { useGetPartnerApplicationSettingsQuery } from 'features/maintenance/services';
import { imagePlace } from 'features/place/constants/StyleConstants';
import { DISABLE_SORTING_PLACE_COLUMNS } from 'features/place/constants/TableConstants';
import useDisplayNotification from 'hooks/useDisplayNotification';
import useTable from 'hooks/useTable';
import useToggle from 'hooks/useToggle';
import { ArchiveSlash } from 'iconsax-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import StyledACTable from '../../../../components/StyledACTable/StyledACTable';
import { style } from '../../../../constants/StyleConstants';
import { useAppSelector } from '../../../../hooks/hooks';
import { CurrentUser } from '../../../../store/Auth';
import { handleDefaultPlaceImage, transformACState } from '../../../../utils/helpers';
import { useLazyGetArchivedPlacesQuery, useUnarchivePlaceMutation } from '../../services/index';
import { FilterPropsPlace } from '../TablePlace/TablePlace';
import styles from '../TablePlace/TablePlace.module.scss';
import tableArchivePlaceSettings from './tableArchivePlaceSettings';

const TableArchivePlace = () => {
  const { t } = useTranslation();
  const { displayError, displaySuccess } = useDisplayNotification();
  const tableSettings = useTable(tableArchivePlaceSettings);
  const { selectedRow, setSelectedRow } = tableSettings;
  const [fetchPlace, { data: places, isFetching }] = useLazyGetArchivedPlacesQuery();
  const { roles }: CurrentUser = useAppSelector(({ auth }) => auth);
  const [isArchivePlaceModalOpen, toggleArchive] = useToggle();

  const [unarchivePlace, { isLoading: isLoadingArchive }] = useUnarchivePlaceMutation();

  const system = roles[ROLE_SYSTEM as never];

  const { data } = useGetPartnerApplicationSettingsQuery();

  const placesImageQuality = data?.dashboardImageQualitySettings?.placeList;

  const placesTableData = useMemo(() => {
    return places?.content?.map((place) => {
      const { category, document } = place;
      const imageUrl = document && document[placesImageQuality];
      const imageFile = handleDefaultPlaceImage(category, imageUrl);
      return {
        rawData: place,
        render: {
          ...place,
          name: (
            <div className={styles.place}>
              <img alt={place?.name} src={imageFile} style={imagePlace} />
              <div>{place?.name}</div>
            </div>
          ),
        },
      };
    });
  }, [places?.content]);

  const handleChangeTableState = (tableState: TableStateProps<FilterPropsPlace>) => {
    const tableData = system ? { ...tableState } : { ...tableState, [IS_EMPLOYED]: 'true' };
    fetchPlace(transformACState(tableData, FORM_ELEMENT_NAME));
  };

  const handleUnarchive = ({ rowData }: { rowData: any }) => [
    setSelectedRow(rowData),
    toggleArchive(),
  ];

  const handelUnarchivePlace = async () => {
    try {
      await unarchivePlace(selectedRow?.uuid).unwrap();
      toggleArchive();
      displaySuccess({ key: t('archiveModal.activeModalSuccess') });
    } catch (error) {
      const {
        data: { code },
      } = error as ErrorMessage;
      toggleArchive();
      displayError({ key: code });
    }
  };

  const additionalActions = [
    {
      title: t('archiveModal.unarchive'),
      icon: <ArchiveSlash data-testid='unarchiveButton' />,
      tooltip: {
        text: t('place.unarchivePlace'),
      },
      onClick: handleUnarchive,
    },
  ];

  return (
    <>
      <Divider />
      <ACLoading
        isLoading={isFetching}
        loadingElement={<ACSpinner background='var(--text-secondary)' size='xs' />}
      >
        <StyledACTable
          {...tableSettings}
          actions={additionalActions}
          allItemsCount={places?.totalElements}
          defaultSort={{
            field: FORM_ELEMENT_NAME,
            type: 'asc',
          }}
          disableSorting={DISABLE_SORTING_PLACE_COLUMNS}
          onTableStateChange={handleChangeTableState}
          pagesCount={places?.totalPages}
          rows={placesTableData}
        />
      </ACLoading>
      {isArchivePlaceModalOpen && (
        <Modal
          buttonText={t('blockModal.buttonConfirm')}
          closeModal={toggleArchive}
          disabled={isLoadingArchive}
          handleSubmit={handelUnarchivePlace}
          header={t('archiveModal.titleUnarchive')}
          icon={<ArchiveSlash style={style.BlueIconModal} />}
          isShow={isArchivePlaceModalOpen}
          message={t('archiveModal.descriptionUnarchive')}
        />
      )}
    </>
  );
};
export default TableArchivePlace;
